/**
 * Synchronization schedules: which day each step falls on. These are the commonly published beef
 * timelines, kept as recordkeeping schedules only. Products, doses and whether to use a protocol are
 * the ranch's and their veterinarian's call; the round page says so beside the schedule.
 */
import { addDays } from "../../../contract";

export const SYNC_ACTIONS = {
  gnrh: "GnRH shot",
  pg: "PG shot",
  cidr_in: "CIDR in",
  cidr_out: "CIDR out",
  mga_start: "Start feeding MGA",
  mga_end: "Last day of MGA",
  patch: "Heat detection aids on",
  heat_watch: "Watch for heats and breed",
  other: "Other step",
} as const;
export type SyncAction = keyof typeof SYNC_ACTIONS;

export interface ProtocolStep {
  day: number;
  actions: SyncAction[];
  /** The insemination step: recorded as AI services, not as a sync step. */
  ai?: "timed" | "heat";
  note?: string;
}

export interface ProtocolDef { label: string; steps: ProtocolStep[] }

export const PROTOCOLS: Record<string, ProtocolDef> = {
  co_synch_cidr_7: {
    label: "7-day CO-Synch + CIDR",
    steps: [
      { day: 0, actions: ["gnrh", "cidr_in"] },
      { day: 7, actions: ["pg", "cidr_out"] },
      { day: 10, actions: ["gnrh"], ai: "timed", note: "Timed AI 60 to 66 hours after CIDR out" },
    ],
  },
  co_synch_cidr_5: {
    label: "5-day CO-Synch + CIDR",
    steps: [
      { day: 0, actions: ["gnrh", "cidr_in"] },
      { day: 5, actions: ["pg", "cidr_out"] },
      { day: 5, actions: ["pg"], note: "Second PG shot 8 hours or more after the first" },
      { day: 8, actions: ["gnrh"], ai: "timed", note: "Timed AI 72 hours after CIDR out" },
    ],
  },
  select_synch_cidr: {
    label: "Select Synch + CIDR",
    steps: [
      { day: 0, actions: ["gnrh", "cidr_in"] },
      { day: 7, actions: ["pg", "cidr_out"] },
      { day: 7, actions: ["heat_watch"], ai: "heat", note: "Breed on heat for 3 to 4 days" },
    ],
  },
  mga_pg: {
    label: "MGA-PG (heifers)",
    steps: [
      { day: 0, actions: ["mga_start"] },
      { day: 13, actions: ["mga_end"] },
      { day: 32, actions: ["pg"] },
      { day: 32, actions: ["heat_watch"], ai: "heat", note: "Breed on heat for 5 to 6 days" },
    ],
  },
  cidr_pg_14: {
    label: "14-day CIDR-PG",
    steps: [
      { day: 0, actions: ["cidr_in"] },
      { day: 14, actions: ["cidr_out"] },
      { day: 30, actions: ["pg"] },
      { day: 33, actions: ["gnrh"], ai: "timed", note: "Timed AI 66 hours after PG" },
    ],
  },
  other: { label: "Other or custom", steps: [] },
};

export const PROTOCOL_LABELS: Record<string, string> = Object.fromEntries(Object.entries(PROTOCOLS).map(([k, v]) => [k, v.label]));

/** Strings built from the tables above, for the localization check. */
export const PROTOCOL_MESSAGES: string[] = [
  ...Object.values(SYNC_ACTIONS),
  ...Object.values(PROTOCOLS).flatMap((p) => [p.label, ...p.steps.map((s) => s.note).filter((n): n is string => !!n)]),
];

export interface ScheduledStep extends ProtocolStep { index: number; on: string }

/** Dated steps for a round: the protocol's own steps, or the custom ones saved on the job. */
export function scheduleFor(protocol: string | undefined, day0: string, custom?: ProtocolStep[]): ScheduledStep[] {
  const steps = protocol && protocol !== "other" && PROTOCOLS[protocol] ? PROTOCOLS[protocol].steps : custom ?? [];
  return steps.map((s, index) => ({ ...s, index, on: addDays(day0, s.day) }));
}

/** The planned insemination day, when the schedule has one. */
export function aiDayFor(protocol: string | undefined, day0: string, custom?: ProtocolStep[]): string | null {
  return scheduleFor(protocol, day0, custom).find((s) => s.ai)?.on ?? null;
}
